import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

export default class ApprovableModel extends Model {
  /*************************
   **  Attributes          **
   *************************/

  @attr declare approval: string;
  @attr declare state: string;
  @attr declare dataState: string;
  @attr declare transitionLog: Record<string, unknown>[];
  @attr declare affirmed: Record<string, unknown>;
  @attr declare currentReviewers: Record<string, unknown>;
  @attr declare possibleReviewers: Record<string, unknown>;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;
  @attr declare createdBy: Record<string, unknown>;
  @attr declare updatedBy: Record<string, unknown>;

  /*************************
   **  Relationships      **
   *************************/

  // Polymorphic: type resolved from JSONAPI payload (TasAsset, etc.)
  // Empty string because no base model exists; inverse: null makes collision moot
  @belongsTo('', { async: false, inverse: null, polymorphic: true })
  declare approvable: Model;

  // Renamed from `context` — JSONAPI::Resource reserves `context` internally (PR #6789)
  // Polymorphic: type resolved from JSONAPI payload (TasAssistance, TasApplication, etc.)
  @belongsTo('', { async: false, inverse: null, polymorphic: true })
  declare inContext: Model;

  @belongsTo('company', { async: false, inverse: null })
  declare company: Model;

  @belongsTo('person', { async: false, inverse: null })
  declare person: Model;

  /*************************
   **  Computed Properties **
   *************************/

  // NOTE: AFFIRMED and NOT_AFFIRMED are transitory states (PR #6795).
  // They auto-cascade and should never be seen at rest.
  // PREPARING = "changes needed" (employee must act after denial)
  // PENDING = "under review" (reviewer must act)

  get isPreparing() {
    return this.state === 'ApprovableState.PREPARING';
  }

  get isPending() {
    return this.state === 'ApprovableState.PENDING';
  }

  get isInReview() {
    return this.isPreparing || this.isPending;
  }

  get isApproved() {
    return this.state === 'ApprovableState.APPROVED';
  }

  get isRejected() {
    return this.state === 'ApprovableState.REJECTED';
  }

  get isAbandoned() {
    return this.state === 'ApprovableState.ABANDONED';
  }

  get isCourseCashUsageReview() {
    return this.approval === 'ApprovalType.COURSE_CASH_USAGE_REVIEW';
  }

  [Type] = 'tas-approvable' as const;
}
